import { useState, useRef, useEffect, useCallback } from "react";

// ─── CONFIG ────────────────────────────────────────────────────────────────
const BACKEND_URL = "https://musicapp-backend-223412625970.us-central1.run.app";
const MAX_CHARS = 4000;
const MIN_CHARS = 10;
const HISTORY_KEY = "musicapp_history";
const HISTORY_TTL = 24 * 60 * 60 * 1000;

// localStorage keys
const KEY_TRIAL   = "chittimusic_trial";    // number 0,1,2 — lifetime, never resets
const KEY_BALANCE = "chittimusic_balance";  // { clip: N, pro: N } — paid song credits
const KEY_THEME   = "chittimusic_theme";    // "dark" | "light"
const KEY_PROMO_CLAIMED = "chittimusic_promo_claimed"; // "1" once a free promo has been claimed on this device

// Razorpay — one-time payments only, no subscriptions
const RZP_KEY = "rzp_live_SzDTGG6uT555jW";
const PACKAGES = {
  pkg_5sp:  { amount: 99,  clip: 3,  pro: 2,  label: "5 Songs",  tag: "5SP",  desc: "3 clip + 2 pro" },
  pkg_10sp: { amount: 149, clip: 6,  pro: 4,  label: "10 Songs", tag: "10SP", desc: "6 clip + 4 pro" },
  pkg_25sp: { amount: 299, clip: 15, pro: 10, label: "25 Songs", tag: "25SP", desc: "15 clip + 10 pro" },
};

// ─── PROMO (FREE PACKAGE) CONFIG ────────────────────────────────────────────
// Reusable for future promotions — just update these 3 values and redeploy.
// Deadline is stored as a UTC ISO timestamp. To convert "midnight IST" to UTC,
// subtract 5 hours 30 minutes (IST = UTC+5:30), so midnight IST = 18:30 UTC
// on the previous calendar day.
const PROMO_ENABLED  = true;
const PROMO_DEADLINE = "2026-06-14T18:30:00.000Z"; // Midnight IST, June 14 → 15, 2026
const PROMO_PACKAGE  = "pkg_10sp"; // which package this promo grants for free

function isPromoActive() {
  if (!PROMO_ENABLED) return false;
  return Date.now() < new Date(PROMO_DEADLINE).getTime();
}

function hasClaimedPromo() {
  try { return localStorage.getItem(KEY_PROMO_CLAIMED) === "1"; }
  catch { return false; }
}

function markPromoClaimed() {
  try { localStorage.setItem(KEY_PROMO_CLAIMED, "1"); } catch {}
}

// Trial: 1 clip song only — pro songs are paid only
const TRIAL_CLIP_LIMIT = 1;
const TRIAL_PRO_LIMIT  = 0;

// ─── TRIAL HELPERS ──────────────────────────────────────────────────────────
function getTrial() {
  try {
    const raw = localStorage.getItem(KEY_TRIAL);
    if (!raw) return { clip: 0, pro: 0 };
    const parsed = JSON.parse(raw);
    // Handle old format (plain number from previous version)
    // If it was >= 2 (old trial exhausted), treat as clip used
    if (typeof parsed === "number") {
      const clipped = parsed >= 1 ? 1 : 0;
      const result = { clip: clipped, pro: 0 };
      // Migrate to new format immediately
      localStorage.setItem(KEY_TRIAL, JSON.stringify(result));
      return result;
    }
    return parsed;
  } catch { return { clip: 0, pro: 0 }; }
}

function incrementTrial(durationType) {
  try {
    const t = getTrial();
    if (durationType === "3min") t.pro = (t.pro || 0) + 1;
    else                         t.clip = (t.clip || 0) + 1;
    localStorage.setItem(KEY_TRIAL, JSON.stringify(t));
  } catch {}
}

function canUseTrial(durationType) {
  const t = getTrial();
  if (durationType === "3min") return (t.pro || 0) < TRIAL_PRO_LIMIT;
  return (t.clip || 0) < TRIAL_CLIP_LIMIT;
}

function trialRemaining() {
  const t = getTrial();
  const clipLeft = Math.max(0, TRIAL_CLIP_LIMIT - (t.clip || 0));
  const proLeft  = Math.max(0, TRIAL_PRO_LIMIT  - (t.pro  || 0));
  return { clip: clipLeft, pro: proLeft, total: clipLeft + proLeft };
}

// ─── BALANCE (PAID) HELPERS ─────────────────────────────────────────────────
function getBalance() {
  try {
    const raw = localStorage.getItem(KEY_BALANCE);
    if (!raw) return { clip: 0, pro: 0 };
    return JSON.parse(raw);
  } catch { return { clip: 0, pro: 0 }; }
}

function addBalance(clip, pro) {
  try {
    const b = getBalance();
    b.clip = (b.clip || 0) + clip;
    b.pro  = (b.pro  || 0) + pro;
    localStorage.setItem(KEY_BALANCE, JSON.stringify(b));
  } catch {}
}

function decrementBalance(durationType) {
  try {
    const b = getBalance();
    if (durationType === "3min") b.pro  = Math.max(0, (b.pro  || 0) - 1);
    else                         b.clip = Math.max(0, (b.clip || 0) - 1);
    localStorage.setItem(KEY_BALANCE, JSON.stringify(b));
  } catch {}
}

function hasPaidBalance(durationType) {
  const b = getBalance();
  if (durationType === "3min") return (b.pro  || 0) > 0;
  return (b.clip || 0) > 0;
}

function hasAnyBalance() {
  const b = getBalance();
  return (b.clip || 0) > 0 || (b.pro || 0) > 0;
}

function canGenerate(durationType) {
  return hasPaidBalance(durationType) || canUseTrial(durationType);
}

function canUsePro() {
  return hasPaidBalance("3min") || canUseTrial("3min");
}

// ─── THEME HELPERS ───────────────────────────────────────────────────────────
function getSavedTheme() {
  try { return localStorage.getItem(KEY_THEME) || "dark"; }
  catch { return "dark"; }
}

// ─── HISTORY HELPERS ────────────────────────────────────────────────────────
function saveToHistory(songId, scene, duration) {
  try {
    const history = getHistory();
    const entry = { songId, scene: scene.trim().slice(0, 80), duration, createdAt: Date.now() };
    const updated = [entry, ...history].slice(0, 20);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  } catch {}
}

function getHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const cutoff = Date.now() - HISTORY_TTL;
    return JSON.parse(raw).filter((s) => s.createdAt > cutoff);
  } catch { return []; }
}

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

function expiresIn(ts) {
  const remaining = HISTORY_TTL - (Date.now() - ts);
  if (remaining <= 0) return "Expired";
  const hrs  = Math.floor(remaining / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  return hrs > 0 ? `Expires in ${hrs}h ${mins}m` : `Expires in ${mins}m`;
}

// ─── ROTATING MESSAGES ──────────────────────────────────────────────────────
const GENERATING_MESSAGES = [
  "Composing your song…",
  "Adding the melody…",
  "Setting the mood…",
  "Weaving in the rhythm…",
  "Almost ready…",
];

// ─── FAQ DATA ────────────────────────────────────────────────────────────────
const FAQ_PROMPTS = [
  {
    category: "🎉 Celebrations",
    examples: [
      "A joyful birthday song for my best friend turning 30, upbeat pop with catchy chorus, English vocals",
      "Diwali celebration song with family, festive and traditional feel, Hindi lyrics with dhol beats",
      "Wedding anniversary song, 10 years of love, romantic ballad with soft piano, English",
    ],
  },
  {
    category: "🎭 Moods & Moments",
    examples: [
      "Rainy evening in a Mumbai café, nostalgic and peaceful, soft jazz with female vocals",
      "Morning motivation song, energetic and uplifting, pop-rock style, English",
      "Missing someone far away, bittersweet and emotional, acoustic guitar, Hindi vocals",
    ],
  },
  {
    category: "🎵 Style & Genre",
    examples: [
      "Bollywood-style love song, 90s feel, melodious with sitar and tabla, Hindi lyrics",
      "EDM party anthem, high energy, build-up and drop, English with crowd chants",
      "Peaceful devotional song, meditative and soulful, soft harmonium and flute",
    ],
  },
  {
    category: "💡 Pro Tips",
    tips: [
      "Include the mood: 'joyful', 'melancholic', 'energetic', 'peaceful'",
      "Mention instruments: 'with guitar', 'tabla beats', 'soft piano', 'dhol'",
      "Describe the tempo: 'slow ballad', 'upbeat', 'mid-tempo groove'",
      "Give context: 'for a wedding', 'for my daughter', 'for a road trip'",
      "You can also paste complete song lyrics — ChittiMusic will set them to music",
    ],
  },
];

// ─── THEME DEFINITIONS ───────────────────────────────────────────────────────
const DARK = {
  bg:          "linear-gradient(135deg, #050a18 0%, #080f22 50%, #050a18 100%)",
  surface:     "rgba(255,255,255,0.06)",
  surfaceAlt:  "rgba(255,255,255,0.04)",
  surfaceCard: "rgba(255,255,255,0.05)",
  border:      "rgba(224,64,251,0.25)",
  borderFaint: "rgba(224,64,251,0.15)",
  borderCard:  "rgba(255,255,255,0.12)",
  text:        "#ffffff",
  textMuted:   "rgba(255,255,255,0.55)",
  textFaint:   "rgba(255,255,255,0.3)",
  textHint:    "rgba(255,255,255,0.45)",
  accent1:     "#e040fb",
  accent2:     "#00d4ff",
  grad:        "linear-gradient(90deg, #e040fb, #00d4ff)",
  gradTag:     "linear-gradient(90deg, #e040fb, #c724e8, #00d4ff)",
  placeholder: "rgba(255,255,255,0.3)",
  micIdle:     "rgba(224,64,251,0.2)",
  btnInactive: "rgba(255,255,255,0.08)",
  btnInactiveText: "rgba(255,255,255,0.3)",
  pill:        "rgba(224,64,251,0.2)",
  chipActive:  "linear-gradient(90deg,rgba(224,64,251,0.25),rgba(0,212,255,0.25))",
  chipBorder:  "rgba(255,255,255,0.15)",
  nudgeBg:     "linear-gradient(135deg,rgba(224,64,251,0.12),rgba(0,212,255,0.12))",
  nudgeBorder: "rgba(224,64,251,0.3)",
  backBtn:     "rgba(255,255,255,0.4)",
  settingsBorder: "rgba(224,64,251,0.4)",
  expiry:      "rgba(0,212,255,0.7)",
  shadow:      "rgba(224,64,251,0.5)",
  planHighBg:  "linear-gradient(135deg,rgba(224,64,251,0.18),rgba(0,212,255,0.18))",
  planHighBorder: "rgba(224,64,251,0.6)",
  faqBorder:   "rgba(224,64,251,0.15)",
  adminBorder: "rgba(224,64,251,0.15)",
  warnBg:      "rgba(251,191,36,0.1)",
  warnBorder:  "rgba(251,191,36,0.3)",
  warnText:    "rgba(251,191,36,0.9)",
};

const LIGHT = {
  bg:          "#f5f5f7",
  surface:     "#ffffff",
  surfaceAlt:  "#f0f0f5",
  surfaceCard: "#ffffff",
  border:      "rgba(224,64,251,0.4)",
  borderFaint: "rgba(224,64,251,0.2)",
  borderCard:  "rgba(0,0,0,0.1)",
  text:        "#111111",
  textMuted:   "rgba(0,0,0,0.55)",
  textFaint:   "rgba(0,0,0,0.38)",
  textHint:    "rgba(0,0,0,0.5)",
  accent1:     "#c000d8",
  accent2:     "#0098cc",
  grad:        "linear-gradient(90deg, #c000d8, #0098cc)",
  gradTag:     "linear-gradient(90deg, #c000d8, #9000b0, #0098cc)",
  placeholder: "rgba(0,0,0,0.3)",
  micIdle:     "rgba(192,0,216,0.15)",
  btnInactive: "rgba(0,0,0,0.08)",
  btnInactiveText: "rgba(0,0,0,0.3)",
  pill:        "rgba(192,0,216,0.12)",
  chipActive:  "linear-gradient(90deg,rgba(192,0,216,0.15),rgba(0,152,204,0.15))",
  chipBorder:  "rgba(0,0,0,0.15)",
  nudgeBg:     "linear-gradient(135deg,rgba(192,0,216,0.08),rgba(0,152,204,0.08))",
  nudgeBorder: "rgba(192,0,216,0.3)",
  backBtn:     "rgba(0,0,0,0.4)",
  settingsBorder: "rgba(192,0,216,0.4)",
  expiry:      "rgba(0,152,204,0.8)",
  shadow:      "rgba(192,0,216,0.4)",
  planHighBg:  "linear-gradient(135deg,rgba(192,0,216,0.1),rgba(0,152,204,0.1))",
  planHighBorder: "rgba(192,0,216,0.5)",
  faqBorder:   "rgba(192,0,216,0.15)",
  adminBorder: "rgba(192,0,216,0.15)",
  warnBg:      "rgba(180,120,0,0.08)",
  warnBorder:  "rgba(180,120,0,0.25)",
  warnText:    "rgba(140,90,0,0.9)",
};

// ─── KEYFRAME INJECTION ─────────────────────────────────────────────────────
const injectStyles = () => {
  if (document.getElementById("musicapp-keyframes")) return;
  const el = document.createElement("style");
  el.id = "musicapp-keyframes";
  el.textContent = `
    @keyframes pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.7;transform:scale(1.1)} }
    @keyframes walk {
      0%   { transform: translateY(0px)  rotate(-1.5deg) translateX(0px); }
      25%  { transform: translateY(-5px) rotate(0deg)    translateX(3px);  }
      50%  { transform: translateY(-1px) rotate(1.5deg)  translateX(0px); }
      75%  { transform: translateY(-5px) rotate(0deg)    translateX(-3px); }
      100% { transform: translateY(0px)  rotate(-1.5deg) translateX(0px); }
    }
    * { box-sizing: border-box; }
    body { margin: 0; padding: 0; }
    audio { display: none; }
    .cm-grad-dark {
      background: linear-gradient(90deg, #e040fb, #00d4ff);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      display: inline-block;
    }
    .cm-grad-dark-tag {
      background: linear-gradient(90deg, #e040fb, #c724e8, #00d4ff);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      display: block;
    }
    .cm-grad-light {
      background: linear-gradient(90deg, #c000d8, #0098cc);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      display: inline-block;
    }
    .cm-grad-light-tag {
      background: linear-gradient(90deg, #c000d8, #9000b0, #0098cc);
      -webkit-background-clip: text;
      background-clip: text;
      -webkit-text-fill-color: transparent;
      color: transparent;
      display: block;
    }
    textarea::placeholder { color: rgba(255,255,255,0.3); }
  `;
  document.head.appendChild(el);
};

const fmtTime = (s) => {
  if (!s || isNaN(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════════════════════════════
export default function App() {
  injectStyles();

  const [theme, setTheme] = useState(getSavedTheme());
  const T = theme === "dark" ? DARK : LIGHT;

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    try { localStorage.setItem(KEY_THEME, next); } catch {}
  };

  // screen: "home"|"settings"|"generating"|"playback"|"history"|"upgrade"|"terms"|"privacy"|"faq"
  const [screen, setScreen] = useState("home");
  const [faqSection, setFaqSection] = useState(null);

  const [scene, setScene]     = useState("");
  const [lang, setLang]       = useState("EN");
  const [duration, setDuration] = useState("30sec");
  const [recording, setRecording] = useState(false);

  const [audioUrl, setAudioUrl]           = useState(null);
  const [currentSongId, setCurrentSongId] = useState(null);
  const [error, setError]                 = useState(null);
  const [msgIndex, setMsgIndex]           = useState(0);
  const [historyLoadingId, setHistoryLoadingId] = useState(null);

  const [playing, setPlaying]       = useState(false);
  const [progress, setProgress]     = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration_, setDuration_]   = useState(0);

  const [historyList, setHistoryList] = useState([]);
  const [balanceState, setBalanceState] = useState(getBalance());
  const [promoClaimedState, setPromoClaimedState] = useState(hasClaimedPromo());
  const [promoActiveState, setPromoActiveState] = useState(isPromoActive());

  const audioRef       = useRef(null);
  const recognitionRef = useRef(null);

  // ── Load Razorpay script on mount
  useEffect(() => {
    if (document.getElementById("razorpay-script")) return;
    const s = document.createElement("script");
    s.id = "razorpay-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  // ── Rotate generating messages
  useEffect(() => {
    if (screen !== "generating") return;
    const t = setInterval(() => setMsgIndex((i) => (i + 1) % GENERATING_MESSAGES.length), 3000);
    return () => clearInterval(t);
  }, [screen]);

  // ── Load history on screen open
  useEffect(() => {
    if (screen === "history") setHistoryList(getHistory());
  }, [screen]);

  // ── Re-check promo active status periodically (so the button can disappear
  //    on its own once the deadline passes, without needing a page refresh)
  useEffect(() => {
    const t = setInterval(() => setPromoActiveState(isPromoActive()), 60000);
    return () => clearInterval(t);
  }, []);

  // ── Audio listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime   = () => { setCurrentTime(audio.currentTime); setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0); };
    const onLoaded = () => setDuration_(audio.duration);
    const onEnded  = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("ended", onEnded);
    return () => { audio.removeEventListener("timeupdate", onTime); audio.removeEventListener("loadedmetadata", onLoaded); audio.removeEventListener("ended", onEnded); };
  }, [audioUrl]);

  // ── Open Razorpay — one-time payment
  const openRazorpay = useCallback((pkgKey) => {
    const pkg = PACKAGES[pkgKey];
    if (!pkg) return;
    if (!window.Razorpay) { alert("Payment is loading. Please try again in a moment."); return; }

    const options = {
      key:         RZP_KEY,
      amount:      pkg.amount * 100,
      currency:    "INR",
      name:        "ChittiMusic",
      description: `${pkg.label} Package — ${pkg.desc}`,
      image:       "/musicman.png",
      theme:       { color: "#e040fb" },
      notes:       { package: pkgKey },
      prefill:     {},
      handler: function(response) {
        try {
          addBalance(pkg.clip, pkg.pro);
          setBalanceState(getBalance());
          setScreen("home");
          setTimeout(() => alert(`🎉 ${pkg.label} unlocked! You have ${pkg.clip} clip + ${pkg.pro} pro songs ready to use. Download each song after generating — they are stored for 24 hours only.`), 300);
        } catch(e) { console.error("Balance save error:", e); }
      },
      modal: { ondismiss: function() {} },
    };
    new window.Razorpay(options).open();
  }, []);

  // ── Claim free promo package (no payment) ──
  const claimPromo = useCallback(() => {
    if (!isPromoActive() || hasClaimedPromo()) return;
    const pkg = PACKAGES[PROMO_PACKAGE];
    if (!pkg) return;
    try {
      addBalance(pkg.clip, pkg.pro);
      markPromoClaimed();
      setBalanceState(getBalance());
      setPromoClaimedState(true);
      setScreen("home");
      setTimeout(() => alert(`🎉 ${pkg.label} unlocked! You have ${pkg.clip} clip + ${pkg.pro} pro songs ready to use. Download each song after generating — they are stored for 24 hours only.`), 300);
    } catch(e) { console.error("Promo claim error:", e); }
  }, []);

  // ── Voice input
  const toggleMic = () => {
    if (recording) { recognitionRef.current?.stop(); setRecording(false); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice input not supported. Please type instead."); return; }
    const r = new SR();
    r.lang = lang === "HI" ? "hi-IN" : "en-IN";
    r.continuous = false; r.interimResults = false;
    r.onresult = (e) => setScene((prev) => prev ? prev + " " + e.results[0][0].transcript : e.results[0][0].transcript);
    r.onerror  = (e) => { if (e.error !== "no-speech") alert("Didn't catch that — try again or type."); };
    r.onend    = () => setRecording(false);
    recognitionRef.current = r;
    r.start(); setRecording(true);
  };

  // ── Generate
  const generate = useCallback(async () => {
    if (!canGenerate(duration)) { setScreen("upgrade"); return; }

    setError(null); setAudioUrl(null); setCurrentSongId(null);
    setScreen("generating"); setMsgIndex(0);
    setProgress(0); setCurrentTime(0); setDuration_(0); setPlaying(false);

    try {
      const res  = await fetch(`${BACKEND_URL}/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ scene: scene.trim(), language: lang, duration }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Something went wrong.");

      // Deduct from paid balance first, then trial
      if (hasPaidBalance(duration)) {
        decrementBalance(duration);
        setBalanceState(getBalance());
      } else {
        incrementTrial(duration);
      }

      setAudioUrl(data.audioUrl);
      if (data.songId) { setCurrentSongId(data.songId); saveToHistory(data.songId, scene, duration); }
      setTimeout(() => { if (audioRef.current) audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }, 300);
      setScreen("playback");
    } catch(err) {
      setError(err.message || "Our music studio hit a snag. Please try again.");
      setScreen("playback");
    }
  }, [scene, lang, duration]);

  // ── Load from history
  const loadFromHistory = async (entry) => {
    setHistoryLoadingId(entry.songId);
    try {
      const res  = await fetch(`${BACKEND_URL}/song/${entry.songId}`);
      const data = await res.json();
      if (!res.ok || !data.success) {
        alert("This song has expired. Songs are kept for 24 hours only.");
        const cleaned = getHistory().filter((s) => s.songId !== entry.songId);
        localStorage.setItem(HISTORY_KEY, JSON.stringify(cleaned));
        setHistoryList(cleaned); return;
      }
      setScene(entry.scene); setDuration(entry.duration || "30sec");
      setAudioUrl(data.audioUrl); setCurrentSongId(entry.songId);
      setError(null); setProgress(0); setCurrentTime(0); setDuration_(0); setPlaying(false);
      setTimeout(() => { if (audioRef.current) audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }, 300);
      setScreen("playback");
    } catch { alert("Could not load this song. Check your connection."); }
    finally  { setHistoryLoadingId(null); }
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else         { audioRef.current.play();  setPlaying(true);  }
  };

  const seek = (e) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    audioRef.current.currentTime = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width)) * audioRef.current.duration;
  };

  const download = () => {
    if (!audioUrl) return;
    const a = document.createElement("a"); a.href = audioUrl;
    a.download = `ChittiMusic_${Date.now()}.mp3`; a.click();
  };

  const reset = () => {
    setScreen("home"); setScene(""); setAudioUrl(null); setCurrentSongId(null);
    setError(null); setPlaying(false); setProgress(0); setCurrentTime(0); setDuration_(0);
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ""; }
  };

  const songTitle = scene.trim().split(/\s+/).slice(0, 5).join(" ") + (scene.trim().split(/\s+/).length > 5 ? "…" : "");
  const bal       = balanceState;
  const trial     = trialRemaining();
  const hasBalance = hasAnyBalance();
  const showPromoButton = promoActiveState && !promoClaimedState;

  // ── Inline styles using theme
  const s = {
    app:       { minHeight: "100vh", background: T.bg, fontFamily: "'Segoe UI', system-ui, sans-serif", color: T.text, display: "flex", flexDirection: "column", alignItems: "center", padding: "0 0 40px 0", transition: "background 0.3s, color 0.3s" },
    header:    { width: "100%", maxWidth: 480, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "24px 20px 8px" },
    logo:      { fontSize: 22, fontWeight: 700, background: T.grad, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent", letterSpacing: "-0.5px", display: "inline-block" },
    card:      { width: "100%", maxWidth: 480, padding: "0 16px", flex: 1, display: "flex", flexDirection: "column", gap: 16 },
    taglineMain: { fontSize: 26, fontWeight: 700, lineHeight: 1.25, background: T.gradTag, WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent", textAlign: "center", display: "block" },
    taglineSub:  { fontSize: 14, color: T.textHint, marginTop: 6, textAlign: "center" },
    textareaWrap: { position: "relative", background: T.surface, borderRadius: 16, border: `1px solid ${T.border}`, padding: "14px 14px 36px 14px" },
    textarea:  { width: "100%", minHeight: 120, background: "transparent", border: "none", outline: "none", color: T.text, fontSize: 16, lineHeight: 1.5, resize: "none", fontFamily: "inherit", boxSizing: "border-box" },
    ctaBtn: (active) => ({ width: "100%", padding: "16px 0", borderRadius: 14, border: "none", fontSize: 17, fontWeight: 700, letterSpacing: 0.3, cursor: active ? "pointer" : "not-allowed", background: active ? T.grad : T.btnInactive, color: active ? "#fff" : T.btnInactiveText, transition: "all 0.25s", boxShadow: active ? `0 4px 24px ${T.shadow}33` : "none" }),
    trustLine: { textAlign: "center", fontSize: 12, color: T.textFaint, marginTop: -4 },
    chip: (active, locked) => ({ padding: "9px 18px", borderRadius: 24, border: `1px solid ${active ? T.accent1 : T.chipBorder}`, background: active ? T.chipActive : T.surfaceAlt, color: locked ? T.textFaint : active ? T.text : T.textMuted, fontSize: 14, fontWeight: active ? 600 : 400, cursor: locked ? "not-allowed" : "pointer", transition: "all 0.18s", display: "flex", alignItems: "center", gap: 6 }),
    lockedBadge: { fontSize: 10, background: `${T.accent2}33`, color: T.accent2, padding: "2px 7px", borderRadius: 10, fontWeight: 700 },
    backBtn:   { background: "none", border: "none", color: T.backBtn, cursor: "pointer", fontSize: 14, padding: "4px 0", display: "flex", alignItems: "center", gap: 6, marginBottom: 8 },
    sectionLabel: { fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: T.textFaint, textTransform: "uppercase", marginBottom: 8, marginTop: 4 },
    chipRow:   { display: "flex", flexWrap: "wrap", gap: 10 },
    settingsPreview: { background: T.surfaceAlt, borderRadius: 12, padding: "12px 14px", fontSize: 13, color: T.textMuted, lineHeight: 1.6, fontStyle: "italic", borderLeft: `3px solid ${T.settingsBorder}` },
    secondaryBtn: { width: "100%", padding: "13px 0", borderRadius: 14, border: `1px solid ${T.accent1}55`, background: "transparent", color: T.accent1, fontSize: 15, fontWeight: 600, cursor: "pointer", marginTop: 8 },
    downloadBtn: { width: "100%", padding: "15px 0", borderRadius: 14, border: "none", background: T.grad, color: "#fff", fontSize: 16, fontWeight: 700, cursor: "pointer", marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
    errorBox:  { background: "rgba(244,63,94,0.15)", border: "1px solid rgba(244,63,94,0.35)", borderRadius: 12, padding: "14px 16px", fontSize: 14, color: "#fda4af", textAlign: "center" },
    playbackCard: { background: T.surface, borderRadius: 20, padding: 20, border: `1px solid ${T.border}` },
    playPauseBtn: { width: 48, height: 48, borderRadius: "50%", background: T.grad, border: "none", color: "#fff", fontSize: 20, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    progressBar: { height: 4, background: theme === "dark" ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)", borderRadius: 4, overflow: "hidden" },
    upgradeNudge: { background: T.nudgeBg, border: `1px solid ${T.nudgeBorder}`, borderRadius: 14, padding: "14px 16px", textAlign: "center" },
    upgradeBtn: { padding: "9px 24px", borderRadius: 20, border: "none", background: T.grad, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" },
    historyBtn: { width: "100%", padding: "12px 0", borderRadius: 14, border: `1px solid ${T.border}`, background: T.surfaceAlt, color: T.textMuted, fontSize: 14, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: -4 },
    historyCard: { background: T.surfaceCard, borderRadius: 14, border: `1px solid ${T.faqBorder}`, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8 },
    emptyHistory: { textAlign: "center", padding: "40px 20px", color: T.textFaint, fontSize: 14, lineHeight: 1.8 },
    footerLinks: { display: "flex", justifyContent: "center", gap: 12, marginTop: 8, paddingBottom: 4, flexWrap: "wrap" },
    footerLink: { fontSize: 12, color: T.textFaint, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 },
    legalScreen: { width: "100%", maxWidth: 480, padding: "0 16px 40px", display: "flex", flexDirection: "column", gap: 12 },
    faqScreen:  { width: "100%", maxWidth: 480, padding: "0 16px 40px", display: "flex", flexDirection: "column", gap: 12 },
    faqSection: { background: T.surfaceCard, borderRadius: 14, border: `1px solid ${T.faqBorder}`, overflow: "hidden" },
    upgradeScreen: { width: "100%", maxWidth: 480, padding: "0 16px", display: "flex", flexDirection: "column", gap: 16 },
    warnBox:    { background: T.warnBg, border: `1px solid ${T.warnBorder}`, borderRadius: 12, padding: "12px 16px", fontSize: 13, color: T.warnText, lineHeight: 1.7 },
    pkgCard: (highlighted) => ({ background: highlighted ? T.planHighBg : T.surfaceCard, borderRadius: 18, border: highlighted ? `1.5px solid ${T.planHighBorder}` : `1px solid ${T.borderCard}`, padding: "20px 18px", display: "flex", flexDirection: "column", gap: 12, position: "relative" }),
    pkgBtn: (highlighted) => ({ width: "100%", padding: "13px 0", borderRadius: 12, border: highlighted ? "none" : `1px solid ${T.accent1}88`, background: highlighted ? T.grad : "transparent", color: highlighted ? "#fff" : T.accent1, fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }),
    promoBtn: { width: "100%", padding: "13px 0", borderRadius: 12, border: "none", background: "linear-gradient(90deg, #34d399, #10b981)", color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", transition: "all 0.2s" },
    themeBtn: { background: "none", border: `1px solid ${T.borderFaint}`, borderRadius: 20, padding: "5px 12px", cursor: "pointer", fontSize: 16, color: T.textMuted, display: "flex", alignItems: "center", gap: 4 },
  };

  const UpgradeNudge = ({ message }) => (
    <div style={s.upgradeNudge}>
      <div style={{ fontSize: 14, color: T.textMuted, marginBottom: 10 }}>{message}</div>
      <button style={s.upgradeBtn} onClick={() => setScreen("upgrade")}>See Packages →</button>
    </div>
  );

  const PlanFeature = ({ text }) => (
    <div style={{ fontSize: 13, color: T.textMuted, display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: T.accent1, flexShrink: 0, display: "inline-block" }} />
      {text}
    </div>
  );

  return (
    <div style={s.app}>
      <audio ref={audioRef} src={audioUrl || ""} />

      {/* ── HEADER ── */}
      <div style={s.header}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src="/musicman.png" alt="ChittiMusic" style={{ width: 38, height: 38, objectFit: "contain" }} />
          <span className={theme === "dark" ? "cm-grad-dark" : "cm-grad-light"} style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px" }}>ChittiMusic</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button style={s.themeBtn} onClick={toggleTheme} title="Toggle theme">
            {theme === "dark" ? "☀️" : "🌙"}
          </button>
          <div style={{ display: "flex", gap: 4, background: theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)", borderRadius: 20, padding: 3 }}>
            {["EN","HI"].map(l => (
              <button key={l} onClick={() => setLang(l)} style={{ padding: "5px 14px", borderRadius: 16, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: lang === l ? T.grad : "transparent", color: lang === l ? "#fff" : T.textMuted, transition: "all 0.2s" }}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ SCREEN 1 — HOME ═══ */}
      {screen === "home" && (
        <div style={s.card}>
          <div style={{ textAlign: "center", marginTop: 12, marginBottom: 4 }}>
            <div className={theme === "dark" ? "cm-grad-dark-tag" : "cm-grad-light-tag"} style={{ fontSize: 26, fontWeight: 700, lineHeight: 1.25 }}>Describe the moment.<br />Get the music.</div>
            <div style={s.taglineSub}>AI-generated songs in under 30 seconds</div>
          </div>

          <div style={s.textareaWrap}>
            <textarea
              style={{ ...s.textarea, "::placeholder": { color: T.placeholder } }}
              placeholder={lang === "HI"
                ? "जैसे: मेरी बेटी का पहला जन्मदिन, खुशनुमा और मस्तीभरा गाना — या अपने खुद के बोल यहाँ पेस्ट करें…"
                : "e.g. A joyful birthday song for my daughter turning 5, upbeat and fun, English vocals\n\n— OR paste your own song lyrics here and we'll set them to music"
              }
              value={scene}
              onChange={(e) => setScene(e.target.value.slice(0, MAX_CHARS))}
              maxLength={MAX_CHARS}
            />
            <span style={{ position: "absolute", bottom: 10, left: 14, fontSize: 12, color: scene.length >= MIN_CHARS ? T.accent1 : T.textFaint, transition: "color 0.2s" }}>
              {scene.length}/{MAX_CHARS}
            </span>
            <button
              style={{ position: "absolute", bottom: 8, right: 10, width: 36, height: 36, borderRadius: "50%", border: "none", background: recording ? "linear-gradient(135deg,#f43f5e,#e11d48)" : T.micIdle, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, animation: recording ? "pulse 1s infinite" : "none" }}
              onClick={toggleMic} title="Speak your scene"
            >
              {recording ? "⏹" : "🎤"}
            </button>
          </div>

          {recording && <div style={{ textAlign: "center", fontSize: 13, color: "#f87171", marginTop: -8 }}>🔴 Recording… tap mic to stop</div>}

          <button style={s.ctaBtn(scene.length >= MIN_CHARS)} disabled={scene.length < MIN_CHARS}
            onClick={() => scene.length >= MIN_CHARS && setScreen("settings")}>
            Create My Song →
          </button>

          <div style={s.trustLine}>No Login · No Subscription · Lifetime Validity</div>

          {getHistory().length > 0 && (
            <button style={s.historyBtn} onClick={() => setScreen("history")}>
              🕐 Recent Songs ({getHistory().length})
            </button>
          )}

          {/* Balance / trial status */}
          {hasBalance ? (
            <div style={{ textAlign: "center", fontSize: 13, color: T.textMuted, marginTop: 4 }}>
              Balance: <strong style={{ color: T.accent1 }}>{bal.clip}</strong> clip · <strong style={{ color: T.accent2 }}>{bal.pro}</strong> pro songs remaining
            </div>
          ) : trial.total > 0 ? (
            <div style={{ textAlign: "center", fontSize: 13, color: T.textMuted, marginTop: 4 }}>
              {trial.clip > 0 ? "1 free clip song remaining to try" : ""}
            </div>
          ) : (
            <div style={{ textAlign: "center", fontSize: 13, marginTop: 4 }}>
              <span style={{ color: T.textFaint }}>Free trial used. </span>
              <span style={{ color: T.accent1, cursor: "pointer", fontWeight: 600 }} onClick={() => setScreen("upgrade")}>Buy a package →</span>
            </div>
          )}

          <div style={s.footerLinks}>
            <span style={s.footerLink} onClick={() => setScreen("terms")}>Terms</span>
            <span style={{ color: T.textFaint }}>·</span>
            <span style={s.footerLink} onClick={() => setScreen("privacy")}>Privacy</span>
            <span style={{ color: T.textFaint }}>·</span>
            <span style={s.footerLink} onClick={() => setScreen("faq")}>FAQ</span>
            <span style={{ color: T.textFaint }}>·</span>
            <span style={s.footerLink} onClick={() => setScreen("upgrade")}>Buy Songs</span>
          </div>
        </div>
      )}

      {/* ═══ SCREEN 2 — SETTINGS ═══ */}
      {screen === "settings" && (
        <div style={s.card}>
          <button style={s.backBtn} onClick={() => setScreen("home")}>← Back</button>
          <div style={s.settingsPreview}>"{scene.length > 100 ? scene.slice(0, 100) + "…" : scene}"</div>

          <div>
            <div style={s.sectionLabel}>Song Language</div>
            <div style={s.chipRow}>
              {[{id:"EN",label:"English"},{id:"HI",label:"Hindi"}].map(opt => (
                <button key={opt.id} style={s.chip(lang === opt.id, false)} onClick={() => setLang(opt.id)}>{opt.label}</button>
              ))}
            </div>
          </div>

          <div>
            <div style={s.sectionLabel}>Duration</div>
            <div style={s.chipRow}>
              <button style={s.chip(duration === "30sec", false)} onClick={() => setDuration("30sec")}>⏱ 30 seconds</button>
              <button style={s.chip(duration === "3min", !canUsePro())} onClick={() => { if (!canUsePro()) { setScreen("upgrade"); return; } setDuration("3min"); }}>
                🎵 3 minutes
                {!canUsePro() && <span style={s.lockedBadge}>PRO</span>}
              </button>
            </div>
            {!canUsePro() && <div style={{ fontSize: 12, color: T.textFaint, marginTop: 8 }}>3-minute songs require a song package</div>}
          </div>

          {!canGenerate(duration) ? (
            <UpgradeNudge message={`You have no ${duration === "3min" ? "pro" : "clip"} songs remaining. Buy a package to continue.`} />
          ) : (
            <button style={s.ctaBtn(true)} onClick={generate}>🎵 Generate My Song</button>
          )}

          {hasBalance && (
            <div style={{ textAlign: "center", fontSize: 13, color: T.textMuted, marginTop: 4 }}>
              Balance: {bal.clip} clip · {bal.pro} pro songs remaining
            </div>
          )}
          {!hasBalance && trial.total > 0 && (
            <div style={{ textAlign: "center", fontSize: 13, color: T.textMuted, marginTop: 4 }}>
              {duration === "3min" ? `${trial.pro} free pro` : `${trial.clip} free clip`} song{trial.total > 1 ? "s" : ""} remaining
            </div>
          )}
        </div>
      )}

      {/* ═══ SCREEN 3 — GENERATING ═══ */}
      {screen === "generating" && (
        <div style={{ ...s.card, alignItems: "center" }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 28, minHeight: "60vh" }}>
            <img src="/musicman.png" alt="Generating" style={{ width: 180, height: 180, objectFit: "contain", animation: "walk 0.9s linear infinite", filter: `drop-shadow(0 8px 32px ${T.shadow})` }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 600, textAlign: "center", color: T.text, minHeight: 28 }}>{GENERATING_MESSAGES[msgIndex]}</div>
              <div style={{ fontSize: 13, color: T.textMuted, textAlign: "center" }}>{duration === "3min" ? "Full songs take 1–3 minutes — worth the wait ✨" : "This usually takes 15–40 seconds"}</div>
            </div>
            <div style={{ fontSize: 12, color: T.textFaint, textAlign: "center", maxWidth: 260, fontStyle: "italic" }}>"{scene.length > 60 ? scene.slice(0, 60) + "…" : scene}"</div>
          </div>
        </div>
      )}

      {/* ═══ SCREEN 4 — PLAYBACK ═══ */}
      {screen === "playback" && (
        <div style={s.card}>
          <div style={{ marginTop: 8, marginBottom: 4 }}>
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>✅ Your song is ready</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, color: T.text, lineHeight: 1.3 }}>🎵 {songTitle}</div>
          </div>

          {error ? (
            <>
              <div style={s.errorBox}>⚠️ {error}</div>
              <button style={s.ctaBtn(true)} onClick={generate}>Try Again</button>
              <button style={s.secondaryBtn} onClick={reset}>Start Over</button>
            </>
          ) : (
            <>
              <div style={s.playbackCard}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                  <button style={s.playPauseBtn} onClick={togglePlay}>{playing ? "⏸" : "▶"}</button>
                  <div style={{ flex: 1, cursor: "pointer" }} onClick={seek}>
                    <div style={s.progressBar}><div style={{ height: "100%", width: `${progress}%`, background: T.grad, borderRadius: 4, transition: "width 0.3s linear" }} /></div>
                    <div style={{ fontSize: 11, color: T.textFaint, marginTop: 4 }}>{fmtTime(currentTime)} / {fmtTime(duration_)}</div>
                  </div>
                </div>
              </div>
              <button style={s.downloadBtn} onClick={download}>⬇ Download MP3</button>
              <button style={s.secondaryBtn} onClick={() => setScreen("history")}>🕐 Recent Songs</button>
              <button style={s.secondaryBtn} onClick={reset}>🔄 Generate Another</button>
              {!hasBalance && trial.total === 0 && <UpgradeNudge message="You've used all your free songs. Buy a package to keep creating." />}
              {hasBalance && (
                <div style={{ textAlign: "center", fontSize: 13, color: T.textMuted, marginTop: 4 }}>
                  Balance: {bal.clip} clip · {bal.pro} pro songs remaining
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ═══ SCREEN 5 — HISTORY ═══ */}
      {screen === "history" && (
        <div style={s.card}>
          <button style={s.backBtn} onClick={() => setScreen("home")}>← Back</button>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>🕐 Recent Songs</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: -8 }}>Songs are kept for 24 hours. Download before they expire.</div>
          {historyList.length === 0 ? (
            <div style={s.emptyHistory}>No recent songs yet.<br />Generate your first song to see it here.</div>
          ) : historyList.map((entry) => (
            <div key={entry.songId} style={s.historyCard}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                <div style={{ fontSize: 14, color: T.text, fontWeight: 500, flex: 1, lineHeight: 1.4 }}>🎵 {entry.scene}{entry.scene.length >= 80 ? "…" : ""}</div>
                <div style={{ fontSize: 11, color: T.textFaint, whiteSpace: "nowrap" }}>{timeAgo(entry.createdAt)}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 8 }}>
                <div style={{ fontSize: 11, color: T.expiry }}>{expiresIn(entry.createdAt)}</div>
                <button style={{ padding: "7px 16px", borderRadius: 20, border: `1px solid ${T.accent1}66`, background: "transparent", color: T.accent1, fontSize: 13, fontWeight: 600, cursor: historyLoadingId === entry.songId ? "wait" : "pointer", opacity: historyLoadingId === entry.songId ? 0.5 : 1 }}
                  onClick={() => loadFromHistory(entry)} disabled={historyLoadingId === entry.songId}>
                  {historyLoadingId === entry.songId ? "Loading…" : "▶ Play & Download"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══ SCREEN 6 — UPGRADE / BUY SONGS ═══ */}
      {screen === "upgrade" && (
        <div style={s.upgradeScreen}>
          <button style={s.backBtn} onClick={() => setScreen("home")}>← Back</button>

          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <div className={theme === "dark" ? "cm-grad-dark" : "cm-grad-light"} style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>Buy Songs</div>
            <div style={{ fontSize: 14, color: T.textHint, lineHeight: 1.5 }}>One-time payment. No subscription.</div>
          </div>

          {/* Warning box */}
          <div style={s.warnBox}>
            ⚠️ <strong>Important:</strong> Since ChittiMusic has no login, your song balance is stored locally on your device only. If you clear your app or device data, the balance cannot be recovered. Please use your songs before clearing any data.
          </div>

          {/* 5SP */}
          <div style={s.pkgCard(false)}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>5 Songs</div>
              <div style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: `${T.accent1}22`, color: T.accent1 }}>5SP</div>
            </div>
            <div><span style={{ fontSize: 28, fontWeight: 700, color: T.text }}>₹99</span><span style={{ fontSize: 13, color: T.textFaint, marginLeft: 4 }}>one-time</span></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <PlanFeature text="3 clip songs (30 seconds)" />
              <PlanFeature text="2 pro songs (3 minutes)" />
              <PlanFeature text="English/Hindi vocals" />
              <PlanFeature text="MP3 download, no watermark" />
              <PlanFeature text="Download songs after generating" />
            </div>
            <button style={s.pkgBtn(false)} onClick={() => openRazorpay("pkg_5sp")}>Buy 5 Songs — ₹99</button>
          </div>

          {/* 10SP — most popular */}
          <div style={{ ...s.pkgCard(true), marginTop: 8 }}>
            <div style={{ position: "absolute", top: -11, left: "50%", transform: "translateX(-50%)", background: T.grad, color: "#fff", fontSize: 11, fontWeight: 700, padding: "3px 14px", borderRadius: 20, whiteSpace: "nowrap" }}>✨ Most Popular</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>10 Songs</div>
              <div style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: `${T.accent1}22`, color: T.accent1 }}>10SP</div>
            </div>
            <div><span style={{ fontSize: 28, fontWeight: 700, color: T.text }}>₹149</span><span style={{ fontSize: 13, color: T.textFaint, marginLeft: 4 }}>one-time</span></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <PlanFeature text="6 clip songs (30 seconds)" />
              <PlanFeature text="4 pro songs (3 minutes)" />
              <PlanFeature text="English/Hindi vocals" />
              <PlanFeature text="MP3 download, no watermark" />
              <PlanFeature text="Download songs after generating" />
            </div>
            <button style={s.pkgBtn(true)} onClick={() => openRazorpay("pkg_10sp")}>Buy 10 Songs — ₹149</button>
            {showPromoButton && (
              <button style={s.promoBtn} onClick={claimPromo}>🎁 Claim 10 Songs FREE — Today Only</button>
            )}
          </div>

          {/* 25SP */}
          <div style={{ ...s.pkgCard(false), marginTop: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>25 Songs</div>
              <div style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: `${T.accent2}22`, color: T.accent2 }}>25SP</div>
            </div>
            <div><span style={{ fontSize: 28, fontWeight: 700, color: T.text }}>₹299</span><span style={{ fontSize: 13, color: T.textFaint, marginLeft: 4 }}>one-time</span></div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <PlanFeature text="15 clip songs (30 seconds)" />
              <PlanFeature text="10 pro songs (3 minutes)" />
              <PlanFeature text="English/Hindi vocals" />
              <PlanFeature text="MP3 download, no watermark" />
              <PlanFeature text="Download songs after generating" />
            </div>
            <button style={s.pkgBtn(false)} onClick={() => openRazorpay("pkg_25sp")}>Buy 25 Songs — ₹299</button>
          </div>

          <div style={{ textAlign: "center", fontSize: 12, color: T.textFaint, lineHeight: 1.6, padding: "0 8px" }}>
            Payments processed securely by Razorpay.<br />
            One-time charge — no subscription, no auto-renewal.<br />
            For queries: srl.lblogics@gmail.com
          </div>
          <div style={s.footerLinks}>
            <span style={s.footerLink} onClick={() => setScreen("terms")}>Terms of Service</span>
            <span style={{ color: T.textFaint }}>·</span>
            <span style={s.footerLink} onClick={() => setScreen("privacy")}>Privacy Policy</span>
          </div>
        </div>
      )}

      {/* ═══ SCREEN 7 — TERMS ═══ */}
      {screen === "terms" && (
        <div style={s.legalScreen}>
          <button style={s.backBtn} onClick={() => setScreen("home")}>← Back</button>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 2 }}>Terms of Service</div>
          <div style={{ fontSize: 12, color: T.textFaint, marginTop: -4 }}>Effective date: June 11, 2026</div>
          <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.85 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>1. Acceptance of Terms</div>
            By using ChittiMusic, you agree to these Terms of Service. ChittiMusic is operated by LBLOGIC SOLUTIONS OPC (CIN: U63120KA2025OPC205923), Bengaluru, India.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>2. Use of the Service</div>
            ChittiMusic uses AI (Google Lyria 3) to generate music from your text descriptions. You may use generated songs for personal, non-commercial purposes. You must not use ChittiMusic to create defamatory, obscene, threatening, sexually explicit, or religiously offensive content.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>3. Content Responsibility</div>
            LBLOGIC SOLUTIONS OPC is not responsible for AI-generated content based on user inputs. Users who generate controversial or harmful content do so entirely at their own risk.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>4. Ownership of Generated Music</div>
            Songs are for your personal use. You may not resell or commercially distribute AI-generated songs without appropriate licensing. AI-generated songs may not be eligible for traditional copyright protection.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>5. Payments</div>
            ChittiMusic uses one-time song packages. Payments are processed by Razorpay. No subscription or auto-renewal. Song balances are stored locally on your device — we cannot recover lost balances if device data is cleared. All purchases are final. No refunds are possible.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>6. Free Trial</div>
            New users receive 1 free clip song (30 seconds) as a trial. Pro songs (3 minutes) require a paid package. Trial does not reset.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>7. Service Availability</div>
            We aim for continuous availability but do not guarantee uninterrupted service. We are not liable for losses due to service downtime.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>8. Limitation of Liability</div>
            Our liability is limited to the amount paid by you. We are not liable for indirect or consequential damages.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>9. Governing Law</div>
            Governed by Indian law. Disputes subject to courts in Bengaluru, Karnataka.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>10. Contact</div>
            srl.lblogics@gmail.com
          </div>
        </div>
      )}

      {/* ═══ SCREEN 8 — PRIVACY ═══ */}
      {screen === "privacy" && (
        <div style={s.legalScreen}>
          <button style={s.backBtn} onClick={() => setScreen("home")}>← Back</button>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 2 }}>Privacy Policy</div>
          <div style={{ fontSize: 12, color: T.textFaint, marginTop: -4 }}>Effective date: June 11, 2026</div>
          <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.85 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>1. What We Collect</div>
            ChittiMusic requires no login. We do not collect your name, email, or personal information. The following is stored only on your device: trial usage count, song balance (if purchased), recent song history (24 hours), language preference, theme preference.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>2. Song Storage</div>
            Generated songs are temporarily stored on Google Cloud Storage for 24 hours to enable Recent Songs. Automatically deleted after 24 hours. No personal information is attached to song files.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>3. Payment Data</div>
            Payments are safely and securely processed by Razorpay. All Razorpay Terms and Privacy Policy are applicable for payment transactions.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>4. Analytics</div>
            We use Google Analytics for anonymous traffic data — page visits and feature usage. No personally identifiable information is sent.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>5. Text Prompts</div>
            Your prompts are sent to Google Lyria 3 to generate music. We log prompts temporarily for debugging only.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>6. Cookies</div>
            We use device local storage only — no tracking cookies.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>7. Children's Privacy</div>
            ChittiMusic is not directed at children under 13.
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginTop: 6, marginBottom: 2 }}>8. Contact</div>
            srl.lblogics@gmail.com
          </div>
        </div>
      )}

      {/* ═══ SCREEN 9 — FAQ ═══ */}
      {screen === "faq" && (
        <div style={s.faqScreen}>
          <button style={s.backBtn} onClick={() => setScreen("home")}>← Back</button>
          <div style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 2 }}>❓ FAQ & Help</div>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: -4, marginBottom: 4 }}>Everything you need to know about ChittiMusic</div>

          {/* Prompt Tips */}
          {[
            { key: "prompts",    title: "🎵 How to write great prompts" },
            { key: "packages",   title: "💳 How song packages work" },
            { key: "guidelines", title: "⚠️ Content Guidelines" },
          ].map(({ key, title }) => (
            <div key={key} style={s.faqSection}>
              <div style={{ padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
                onClick={() => setFaqSection(faqSection === key ? null : key)}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{title}</div>
                <div style={{ color: T.textFaint, fontSize: 18 }}>{faqSection === key ? "−" : "+"}</div>
              </div>

              {faqSection === "prompts" && key === "prompts" && (
                <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 4 }}>Tap any example to use it as your prompt:</div>
                  {FAQ_PROMPTS.filter(s => s.examples).map((section, si) => (
                    <div key={si}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: T.accent1, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>{section.category}</div>
                      {section.examples.map((ex, ei) => (
                        <div key={ei} style={{ background: `${T.accent1}14`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: T.textMuted, lineHeight: 1.5, cursor: "pointer", border: `1px solid ${T.accent1}22`, marginBottom: 6 }}
                          onClick={() => { setScene(ex); setScreen("home"); }}>"{ex}"</div>
                      ))}
                    </div>
                  ))}
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.accent2, letterSpacing: 1, textTransform: "uppercase", marginTop: 4 }}>💡 Tips</div>
                  {FAQ_PROMPTS.find(s => s.tips)?.tips.map((tip, ti) => (
                    <div key={ti} style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, display: "flex", gap: 8 }}>
                      <span style={{ color: T.accent1, flexShrink: 0 }}>→</span><span>{tip}</span>
                    </div>
                  ))}
                </div>
              )}

              {faqSection === "packages" && key === "packages" && (
                <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { q: "What is the free trial?", a: "Every new user gets 1 free clip song (30 seconds) — no payment needed. Pro songs (3 minutes) require a paid package." },
                    { q: "What are the song packages?", a: "5SP (₹99): 3 clip + 2 pro songs.\n10SP (₹149): 6 clip + 4 pro songs.\n25SP (₹299): 15 clip + 10 pro songs." },
                    { q: "Is there a subscription or auto-renewal?", a: "No. ChittiMusic has no subscription, no monthly charges, no auto-renewal. Every package is a one-time payment." },
                    { q: "What is a clip song vs pro song?", a: "Clip songs are 30 seconds long. Pro songs are up to 3 minutes with full lyrics and vocals. Pro songs cost more to generate, which is why they require a paid package." },
                    { q: "How long are generated songs stored?", a: "Generated songs are stored on our servers for 24 hours only, then permanently deleted. You can replay and download them within 24 hours from the Recent Songs screen. Always download your song immediately after generating it." },
                    { q: "Can I transfer balance to another device?", a: "No. Since ChittiMusic has no login, your song balance is stored in your device's local storage only. It will not transfer to another device." },
                    { q: "What if I clear my device storage or app data?", a: "Your song balance will be permanently lost and cannot be recovered. Always download songs immediately after generating them." },
                    { q: "What is the refund policy?", a: "All purchases are final. No refunds are possible since ChittiMusic has no login system to verify usage. Please use the free trial song before purchasing a package to ensure the app works well for you." },
                  ].map((item, i, arr) => (
                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2, paddingBottom: 10, borderBottom: i < arr.length - 1 ? `1px solid ${T.borderFaint}` : "none" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>{item.q}</div>
                      <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.75, whiteSpace: "pre-line" }}>{item.a}</div>
                    </div>
                  ))}
                  {/* No account no recovery warning */}
                  <div style={{ ...s.warnBox, marginTop: 4 }}>
                    ⚠️ <strong>No Account = No Recovery.</strong> ChittiMusic intentionally has no login to protect your privacy. This means we cannot recover your balance if you clear your device data. Please be careful.
                  </div>
                </div>
              )}

              {faqSection === "guidelines" && key === "guidelines" && (
                <div style={{ padding: "0 16px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.75 }}>ChittiMusic uses Google Lyria 3. The following are strictly prohibited:</div>
                  {[
                    { icon: "🚫", title: "Hate speech", desc: "Content targeting people based on race, religion, caste, gender, or disability." },
                    { icon: "🚫", title: "Violence & threats", desc: "Songs glorifying violence or threatening individuals or groups." },
                    { icon: "🚫", title: "Sexual or explicit content", desc: "Sexually explicit lyrics or content involving minors." },
                    { icon: "🚫", title: "Voice cloning", desc: "Attempting to replicate celebrity or public figure voices without consent." },
                    { icon: "🚫", title: "Defamation", desc: "False claims about real people or organisations." },
                    { icon: "🚫", title: "Political misinformation", desc: "Content spreading false information about elections or governments." },
                    { icon: "ℹ️", title: "SynthID watermark", desc: "All songs contain Google's invisible SynthID watermark. Removing it is prohibited." },
                    { icon: "ℹ️", title: "Copyright disclaimer", desc: "AI-generated songs may not be eligible for copyright protection. You assume liability for commercial use." },
                  ].map((item, i, arr) => (
                    <div key={i} style={{ display: "flex", flexDirection: "column", gap: 2, paddingBottom: 10, borderBottom: i < arr.length - 1 ? `1px solid ${T.borderFaint}` : "none" }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text, display: "flex", gap: 8, marginBottom: 4 }}>{item.icon} {item.title}</div>
                      <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.75 }}>{item.desc}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          <div style={{ fontSize: 12, color: T.textFaint, textAlign: "center", lineHeight: 1.6, marginTop: 4 }}>
            Still have questions? Email srl.lblogics@gmail.com
          </div>
        </div>
      )}

    </div>
  );
}